import { useState } from 'react';
import { PlayerTableViewModelV2 } from 'shared';
import { V2apiClient } from '../../ApiClient';
import { useApiCall } from '../../useApiCall';
import { Panel } from '../../ui/Panel';
import { Button } from '../../ui/Button';
import { Text } from '../../ui/Text';
import { token, zVar } from '../../ui/theme';
import { buildInviteLink, CopyInviteButton } from './invite-button';

/*
 * StartButtons — the pre-game panel shown before a game starts. The host sees
 * the start controls (Start Game is disabled until every seat holds a human);
 * everyone else sees a "waiting for the host" notice. Both get an invite link.
 */

interface Props {
  table: PlayerTableViewModelV2;
}

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: zVar('z-hud'),
  textAlign: 'center',
  display: 'grid',
  gap: 'var(--space-4)',
  padding: 'var(--space-6)',
};

export const StartButtons: React.FC<Props> = ({ table }) => {
  const api = V2apiClient;
  const apiCall = useApiCall();
  const [spectating, setSpectating] = useState(false);
  // Host's choice at start time. Unlocked (the default) leaves the table
  // joinable from the lobby — notably, a newcomer can take over a computer seat.
  // Locking closes it to new players (invite code still admits spectators).
  const [locked, setLocked] = useState(false);

  // Pre-game seats are either a human (Taken) or empty (undefined) — no
  // computers yet — so "all human" is simply "no empty seats".
  const allSeatsFilledWithHumans = table.tablePlayers.every((p) => !!p && !p.isComputer);

  // You can hand back your seat to watch instead — but only if someone else is
  // still seated (there'd be no game to watch otherwise). The board switches to
  // the spectator view on the resulting update, so no navigation is needed here.
  const canSpectate = table.tablePlayers.filter(Boolean).length > 1;
  const spectate = async () => {
    setSpectating(true);
    if (!(await apiCall(api.spectate()))) setSpectating(false);
  };

  const startGame = async (computers: boolean) => {
    await apiCall(api.startGame(computers, locked));
  };

  const spectateButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={spectate}
      disabled={spectating || !canSpectate}
      title={canSpectate ? undefined : 'Someone else must be seated before you can just watch'}
    >
      {spectating ? 'Leaving your seat…' : 'Spectate instead'}
    </Button>
  );

  const invite = (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        justifyItems: 'center',
        borderTop: `1px solid ${token('color-border')}`,
        paddingTop: 'var(--space-4)',
      }}
    >
      <div
        style={{
          maxWidth: 280,
          padding: 'var(--space-2) var(--space-3)',
          borderRadius: 'var(--radius-md)',
          background: token('color-bg'),
          border: `1px solid ${token('color-border')}`,
          color: token('color-muted'),
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={buildInviteLink(table.tableCode)}
      >
        {buildInviteLink(table.tableCode)}
      </div>
      <CopyInviteButton tableCode={table.tableCode} label="Invite a friend" variant="secondary" />
    </div>
  );

  if (!table.youAreCreator) {
    return (
      <Panel variant="hud" style={panelStyle}>
        <Text variant="heading" as="div">
          Waiting for the host to start the game
        </Text>
        <Text variant="muted" as="div">
          Please select a seat.
        </Text>
        {spectateButton}
        {invite}
      </Panel>
    );
  }

  return (
    <Panel variant="hud" style={panelStyle}>
      <Text variant="heading" as="div">
        Ready to play
      </Text>
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Button
          variant="primary"
          size="lg"
          onClick={() => startGame(false)}
          disabled={!allSeatsFilledWithHumans}
          title={
            allSeatsFilledWithHumans ? undefined : 'All seats must be filled with players to start'
          }
        >
          Start Game
        </Button>
        <Button variant="secondary" size="lg" onClick={() => startGame(true)}>
          Play vs Computers
        </Button>
      </div>
      <label
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: token('color-muted'),
          fontSize: '0.85rem',
        }}
      >
        <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
        Lock game — no one can join
      </label>
      {!allSeatsFilledWithHumans && (
        <Text variant="muted" as="div">
          Start Game unlocks once every seat is filled with a player.
        </Text>
      )}
      {spectateButton}
      {invite}
    </Panel>
  );
};
